// @ts-strict-ignore
import { Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { MetaElementUsageIdentityVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { RotateArticlePresenter } from '../../article/presenter/rotate-article.presenter';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { ArticleAttributeUtils } from '@article/util/article-attribute.utils';
import { Injectable } from '@angular/core';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({ providedIn: 'root' })
export class LabelingArticleRotationHelper {
  private readonly LABELING_ROTATION_ANGLE: number = 180;

  constructor(private connectionInfoService: ConnectionInfoService) {}

  public isInvertedLabelingArticleDirection(labelingArticleContainer: ArticleContainer): boolean {
    let isInvertedDirection: boolean = false;
    const backwardConnectionIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
      labelingArticleContainer.articleUsage
    );
    const acceptingConnectionId: MetaElementUsageIdentityVO = backwardConnectionIds.length === 0 ? null : backwardConnectionIds[0];
    const acceptingConnection: Connection = this.connectionInfoService.findConnectionByIdentity(acceptingConnectionId);
    if (acceptingConnection) {
      // v-direction of dynamic article aligned by -X-axis
      labelingArticleContainer.updateMatrixWorld(true);
      const vGivingDirection: Vector3 = labelingArticleContainer
        .localToWorld(new Vector3(-1, 0, 0))
        .sub(labelingArticleContainer.localToWorld(new Vector3(0, 0, 0)));
      vGivingDirection.normalize();
      const acceptingRectangle: Rectangle3D = acceptingConnection.compositionRectangle;
      const uAcceptingDirection: Vector3 = acceptingRectangle.point4.clone().sub(acceptingRectangle.point1);
      uAcceptingDirection.normalize();
      const rotationAngle: number = vGivingDirection.angleTo(uAcceptingDirection) * MathUtils.TO_DEGREES;
      if (MathUtils.compare(Math.abs(rotationAngle), this.LABELING_ROTATION_ANGLE) === 0) {
        isInvertedDirection = true;
      }
    }
    return isInvertedDirection;
  }

  public transformTextureAreaRectangle(textureRectangle: Rectangle3D, articleAppliedRotation: number = NaN, textRotationAngle: number = NaN): void {
    if (!textureRectangle) {
      return;
    }
    if (NumberUtils.isNumber(articleAppliedRotation) && articleAppliedRotation !== 0) {
      MathUtils.rotateRectangle(textureRectangle, -articleAppliedRotation);
    }
    if (NumberUtils.isNumber(textRotationAngle) && textRotationAngle !== 0) {
      MathUtils.rotateRectangle(textureRectangle, -textRotationAngle);
    }
  }

  public isAdditionalRotationRequired(textureRectangle: Rectangle3D): boolean {
    if (!textureRectangle) {
      return false;
    }
    // const uDirection: Vector3 = textureRectangle.point2.clone().sub(textureRectangle.point1);
    // uDirection.normalize();
    const vDirection: Vector3 = textureRectangle.point4.clone().sub(textureRectangle.point1);
    vDirection.normalize();

    // let uCheckVector: Vector3 = null;
    let vCheckVector: Vector3 = null;

    const isPerpendicularAlignment: boolean = this.isPerpendicularAlignmentByXAxis(vDirection);
    // uCheckVector = isPerpendicularAlignment ? new Vector3(1, 0, 0) : new Vector3(0, 0, 1);
    vCheckVector = isPerpendicularAlignment ? new Vector3(0, 0, -1) : new Vector3(1, 0, 0);

    // const uDotProduct: number = uDirection.dot(uCheckVector);
    const vDotProduct: number = vDirection.dot(vCheckVector);

    let rotationRequired: boolean = false;
    if (/*MathUtils.compare(uDotProduct, 0) === -1 || */ MathUtils.compare(vDotProduct, 0) === -1) {
      rotationRequired = true;
    }
    return rotationRequired;
  }

  private isPerpendicularAlignmentByXAxis(uDirection: Vector3): boolean {
    let alignmentAngle: number = uDirection.angleTo(new Vector3(1, 0, 0)) * MathUtils.TO_DEGREES;
    alignmentAngle = Math.abs(alignmentAngle % this.LABELING_ROTATION_ANGLE);
    let isPerpendicularAlignment: boolean = false;
    if (
      MathUtils.compare(alignmentAngle, (this.LABELING_ROTATION_ANGLE / 2) * 0.5) === 1 &&
      MathUtils.compare(alignmentAngle, (this.LABELING_ROTATION_ANGLE / 2) * 1.5) === -1
    ) {
      isPerpendicularAlignment = true;
    }
    return isPerpendicularAlignment;
  }

  /**
   * Applies additional rotation (if it's needed) by fixed 180 degrees to labeling-article to make label-text
   * "readable" in default-view in configurator.
   * For example, additional rotation will be applied in case labeling-article connected to rotated article.
   */
  public rotateLabelingArticle(
      rotateArticlePresenter: RotateArticlePresenter,
    labelingArticleContainer: ArticleContainer,
    recreateConnectionUsages: boolean = true,
    considerOnlyInvolvedGivingConnections: boolean = true
  ): void {
    const correctionIgnored: boolean = ArticleUsageUtils.isLabelingCorrectionIgnored(labelingArticleContainer.articleUsage);
    if (correctionIgnored) {
      // labeling-article doesn't need to be rotated to make text "readable" in case such property exists
      // (mostly related to label-articles in MCS/GDS projects)
      return;
    }

    const textureRectangle: Rectangle3D = this.getTextureAreaRectangle(labelingArticleContainer);
    if (!textureRectangle) {
      return;
    }
    const rotationRequired: boolean = this.isAdditionalRotationRequired(textureRectangle);
    if (rotationRequired) {
      rotateArticlePresenter.rotateArticleContainer(
        labelingArticleContainer,
        this.LABELING_ROTATION_ANGLE,
        false,
        false,
        considerOnlyInvolvedGivingConnections,
        recreateConnectionUsages,
        false,
        null,
        false,
        false
      );
    }
  }

  private getTextureAreaRectangle(labelingArticleContainer: ArticleContainer): Rectangle3D {
    const textureAreas: TextureAreaVO[] = ArticleUtils.getTextTextureElements(labelingArticleContainer.article);
    if (textureAreas.length === 0) {
      return null;
    }
    // uvn-direction aligned the same way for all texture-areas of labeling articles
    const targetTextureArea: TextureAreaVO = textureAreas[0];
    const targetTextureCube: Connection = labelingArticleContainer.findFirstConnectionByName(targetTextureArea.name);
    if (!targetTextureCube) {
      return null;
    }
    const textureRectangle: Rectangle3D = Rectangle3D.fromArray(targetTextureCube.compositionRectangle.toArray());
    const articleAppliedRotation: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      labelingArticleContainer.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    const textRotationAngle: number = ArticleAttributeUtils.getRotationAngleFromArticleInfo(
      ArticleUtils.findArticleInfoByGuid(labelingArticleContainer.articleGUID, labelingArticleContainer.projectModel.currentProject)
    );
    this.transformTextureAreaRectangle(textureRectangle, articleAppliedRotation, textRotationAngle);
    return textureRectangle;
  }
}
